// Bloque «form»: el miembro llena los campos del formulario y se crea un
// registro. Solo se mandan los campos del formulario (los valores fijos y la
// relación con lo elegido los pone el servidor). Los errores del servidor
// llegan en español y se muestran tal cual.

import { useState, type FormEvent } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { siteErrorMessage, type FieldMeta, type SiteBlockOf } from "../values";
import { BlockTitle, siteFieldClass } from "./shared";

type Draft = Record<string, string | boolean | string[]>;

const NUMERIC = new Set(["number", "currency"]);
const BOOLEAN = new Set(["checkbox", "boolean"]);
const INPUT_TYPE: Record<string, string> = { email: "email", url: "url", phone: "tel", date: "date" };

const emptyDraft = (fields: FieldMeta[]): Draft =>
  Object.fromEntries(
    fields.map((f) => [f.key, BOOLEAN.has(f.type) ? false : f.type === "multiselect" ? [] : ""]),
  );

const isBlank = (field: FieldMeta, value: Draft[string] | undefined) =>
  BOOLEAN.has(field.type) ? value !== true : Array.isArray(value) ? value.length === 0 : !String(value ?? "").trim();

/**
 * Valores que se envían: solo los campos del formulario y con valor
 * (números como número). Puro: se prueba sin montar el formulario.
 */
export function formPayload(fields: FieldMeta[], draft: Draft): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    const value = draft[f.key];
    if (BOOLEAN.has(f.type)) {
      if (value === true) out[f.key] = true;
      continue;
    }
    if (Array.isArray(value)) {
      if (value.length) out[f.key] = [...value];
      continue;
    }
    const text = String(value ?? "").trim();
    if (!text) continue;
    if (NUMERIC.has(f.type)) {
      const n = Number(text.replace(",", "."));
      out[f.key] = Number.isFinite(n) ? n : text;
    } else {
      out[f.key] = text;
    }
  }
  return out;
}

function FieldControl({
  id,
  field,
  value,
  invalid,
  disabled,
  onChange,
}: {
  id: string;
  field: FieldMeta;
  value: Draft[string];
  invalid: boolean;
  disabled: boolean;
  onChange: (value: Draft[string]) => void;
}) {
  const common = { id, name: field.key, disabled, "aria-invalid": invalid || undefined, required: field.required };
  if (BOOLEAN.has(field.type)) {
    return (
      <label htmlFor={id} className="flex items-center gap-3 text-[15px] text-foreground">
        <input
          {...common}
          required={false}
          type="checkbox"
          className="size-5 shrink-0 accent-[var(--primary)]"
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
        />
        {field.label}
        {field.required && <RequiredMark />}
      </label>
    );
  }
  if (field.type === "textarea") {
    return (
      <textarea
        {...common}
        rows={4}
        className={cn(siteFieldClass, "min-h-28 resize-y")}
        value={String(value ?? "")}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  if (field.type === "select") {
    return (
      <select {...common} className={siteFieldClass} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)}>
        <option value="">Elige…</option>
        {(field.options ?? []).map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  }
  if (field.type === "multiselect") {
    const selected = Array.isArray(value) ? value : [];
    return (
      <div className="flex flex-wrap gap-2" role="group" aria-labelledby={`${id}-label`}>
        {(field.options ?? []).map((o) => {
          const on = selected.includes(o);
          return (
            <button
              key={o}
              type="button"
              aria-pressed={on}
              disabled={disabled}
              onClick={() => onChange(on ? selected.filter((x) => x !== o) : [...selected, o])}
              className={cn(
                "rounded-full border px-3.5 py-1.5 text-sm transition-colors",
                on ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:bg-accent",
              )}
            >
              {o}
            </button>
          );
        })}
      </div>
    );
  }
  const numeric = NUMERIC.has(field.type);
  return (
    <input
      {...common}
      type={numeric ? "text" : (INPUT_TYPE[field.type] ?? "text")}
      inputMode={numeric ? "decimal" : field.type === "phone" ? "tel" : undefined}
      className={siteFieldClass}
      value={String(value ?? "")}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function RequiredMark() {
  return (
    <span className="ml-0.5 text-destructive" aria-hidden>
      *
    </span>
  );
}

export function FormBlock({
  block,
  onSubmit,
}: {
  block: SiteBlockOf<"form">;
  onSubmit: (values: Record<string, unknown>) => Promise<unknown>;
}) {
  const [draft, setDraft] = useState<Draft>(() => emptyDraft(block.fields));
  const [missing, setMissing] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (sending) return;
    const blank = new Set(block.fields.filter((f) => f.required && isBlank(f, draft[f.key])).map((f) => f.key));
    setMissing(blank);
    if (blank.size) {
      setError(blank.size === 1 ? "Completa el campo obligatorio." : "Completa los campos obligatorios.");
      return;
    }
    setError(null);
    setSending(true);
    try {
      await onSubmit(formPayload(block.fields, draft));
      setSent(true);
      setDraft(emptyDraft(block.fields));
    } catch (err) {
      setError(siteErrorMessage(err, "No pudimos enviar el formulario. Revisa tu conexión e intenta de nuevo."));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <section className="rounded-lg border bg-card p-5 text-center" role="status" data-testid="site-form-success">
        <CheckCircle2 className="mx-auto size-8 text-primary" aria-hidden />
        <p className="mt-2 text-[15px] text-foreground">{block.successText || "Listo, recibimos tu envío."}</p>
        <Button type="button" variant="outline" className="mt-4" onClick={() => setSent(false)}>
          Enviar otro
        </Button>
      </section>
    );
  }

  return (
    <form className="space-y-4 rounded-lg border bg-card p-4 @min-[40rem]:p-5" onSubmit={handleSubmit} noValidate aria-label={block.title}>
      {block.title && <BlockTitle>{block.title}</BlockTitle>}
      {block.link && (
        <p className="text-sm text-muted-foreground">
          {block.link.label ? (
            <>
              Para: <span className="font-medium text-foreground">{block.link.label}</span>
            </>
          ) : (
            "Elige primero a quién corresponde."
          )}
        </p>
      )}
      {block.fields.map((field) => {
        const id = `site-${block.key}-${field.key}`;
        const invalid = missing.has(field.key);
        return (
          <div key={field.key} className="space-y-1.5">
            {!BOOLEAN.has(field.type) && (
              <label id={`${id}-label`} htmlFor={id} className="block text-sm font-medium text-foreground">
                {field.label}
                {field.required && <RequiredMark />}
              </label>
            )}
            <FieldControl
              id={id}
              field={field}
              value={draft[field.key]}
              invalid={invalid}
              disabled={sending}
              onChange={(value) => {
                setDraft((d) => ({ ...d, [field.key]: value }));
                if (invalid) {
                  const rest = new Set([...missing].filter((k) => k !== field.key));
                  setMissing(rest);
                  // Completó lo que faltaba: el aviso general ya no aplica.
                  if (!rest.size) setError(null);
                }
              }}
            />
            {invalid && <p className="text-xs text-destructive">Este campo es obligatorio.</p>}
          </div>
        );
      })}
      {error && (
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
      <Button type="submit" className="h-11 w-full text-base @min-[40rem]:w-auto @min-[40rem]:px-6" disabled={sending}>
        {sending && <Loader2 className="size-4 animate-spin" aria-hidden />}
        {block.submitLabel}
      </Button>
    </form>
  );
}
